import { getLocalApi } from "./localApi";
import { School } from "app/shared/model";
import { Centre } from "app/shared/model";
import apiConfig from "app/api/apiConfig.json";
import { Dayjs } from "dayjs";

type SchoolSummaryDto = {
    id: string | number;
    name: string;
    centreCode: string;
    minimumAge: number;
    minimumStayInWeeks: number;
    lastActiveSchoolInCentre?: boolean;
    entityUpdate?: boolean;
};

type SchoolCentreDto = {
    id: string | number;
    name: string;
    schoolCode: string;
    emergencyNo?: string;
    isActive?: boolean;
    decommissionDate: Dayjs | null;
    lastActiveSchoolInCentre?: boolean;
    entityUpdate: boolean;
};

type SchoolDetailDto = {
    id: string | number;
    name: string;
    centreCode: string;
    schoolCode: string;
    telephoneNo?: string;
    emergencyNo?: string;
    ageFrom?: number;
    ageTo?: number;
    lmsAccess?: boolean;
    opensOnSaturday?: boolean;
    opensOnSunday?: boolean;
    isActive?: boolean;
    line1?: string;
    line2?: string;
    line3?: string;
    line4?: string;
    countryId?: number | string;
    decommissionDate: Dayjs | null;
    lastActiveSchoolInCentre: boolean;
};

function normalizeSchoolIdentifier(value: string | number | null | undefined): string | number | null {
    if (value === null || value === undefined) {
        return null;
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
        return null;
    }
    const match = /^School-(\d+)$/i.exec(trimmed);
    if (match) {
        return match[1];
    }
    return trimmed;
}

function normalizeCentreIdentifier(value: string | number | null | undefined): string | number | null {
    if (value === null || value === undefined) {
        return null;
    }
    const trimmed = String(value).trim();
    if (!trimmed) {
        return null;
    }
    const match = /^CN(\d+)$/i.exec(trimmed);
    if (match) {
        return match[1];
    }
    return trimmed;
}

export async function getAllSchools(): Promise<School[]> {
    const localApi = await getLocalApi();
    return await localApi.fetchList(apiConfig.schools_get_all, mapSchoolSummary, 'Failed to fetch schools');
}

export async function getSchoolsByCentre(centreId: string | number): Promise<School[]> {
    if (centreId == null || centreId === '') {
        return [];
    }
    const localApi = await getLocalApi();
    const requestId = normalizeCentreIdentifier(centreId) ?? centreId;
    const url = apiConfig.schools_get_by_centre.replace('{centreId}', encodeURIComponent(String(requestId)));
    return await localApi.fetchList(url, mapSchoolCentreListing, 'Failed to fetch schools for centre');
}

export async function getSchoolById(id: string | number): Promise<School> {
    const localApi = await getLocalApi();
    const requestId = normalizeSchoolIdentifier(id) ?? id;
    const url = apiConfig.schools_get_by_id.replace('{id}', encodeURIComponent(String(requestId)));
    return await localApi.fetchSingle(url, mapSchoolDetail, 'Failed to fetch school');
}

export async function getAllCentres(): Promise<Centre[]> {
    const localApi = await getLocalApi();
    return await localApi.fetchList(apiConfig.centres_get_all, mapCentre, 'Failed to fetch centres');
}

function mapSchoolSummary(dto: SchoolSummaryDto): School {
    const schoolId = dto.id;
    const normalizedId = normalizeSchoolIdentifier(schoolId) ?? schoolId;
    return {
        id: normalizedId,
        code: String(schoolId),
        name: dto.name,
        centreCode: dto.centreCode,
        minimumAge: Number(dto.minimumAge ?? 0),
        minimumStayInWeeks: Number(dto.minimumStayInWeeks ?? 0),
        excludedBookingSystemIds: [],
        decommissionDate: null,
        lastActiveSchoolInCentre: !!(dto.lastActiveSchoolInCentre ?? false),
        entityUpdate: !!(dto.entityUpdate ?? false),
    };
}

function mapSchoolCentreListing(dto: SchoolCentreDto): School {
    return {
        id: normalizeSchoolIdentifier(dto.id) ?? dto.id,
        code: dto.schoolCode ?? String(dto.id ?? ''),
        name: dto.name,
        centreCode: '',
        minimumAge: 0,
        minimumStayInWeeks: 0,
        emergencyNo: dto.emergencyNo,
        isActive: dto.isActive,
        decommissionDate: dto.decommissionDate,
        lastActiveSchoolInCentre: !!(dto.lastActiveSchoolInCentre ?? false),
        excludedBookingSystemIds: [],
        entityUpdate: !!(dto.entityUpdate ?? false),
    };
}

function mapSchoolDetail(dto: SchoolDetailDto): School {
    return {
        id: normalizeSchoolIdentifier(dto.id) ?? dto.id,
        code: dto.schoolCode,
        name: dto.name,
        centreCode: dto.centreCode,
        minimumAge: Number(dto.ageFrom ?? 0),
        minimumStayInWeeks: 0,
        telephoneNo: dto.telephoneNo,
        emergencyNo: dto.emergencyNo,
        ageFrom: dto.ageFrom,
        ageTo: dto.ageTo,
        hasLmsAccess: dto.lmsAccess,
        opensOnSaturday: dto.opensOnSaturday,
        opensOnSunday: dto.opensOnSunday,
        isActive: dto.isActive,
        address: {
            line1: dto.line1,
            line2: dto.line2,
            line3: dto.line3,
            line4: dto.line4,
            countryId: dto.countryId,
        },
        excludedBookingSystemIds: Array.isArray((dto as any)?.excludedBookingSystemIds)
          ? (dto as any).excludedBookingSystemIds.map((id: number | string) => String(id))
          : [],
        decommissionDate: dto.decommissionDate,
        lastActiveSchoolInCentre: dto.lastActiveSchoolInCentre,
        entityUpdate: !!(dto as any).entityUpdate,
    };
}

function mapCentre(dto: any): Centre {
    const isActive = dto.IsActive ?? dto.isActive ?? dto.active;
    const telephoneNo = dto.telephoneNo ?? dto.telephone ?? dto.phone ?? null;
    const emergencyNo =
        dto.emergencyNo ?? dto.emergencyTelephone ?? dto.emergencyPhone ?? null;
    const centreCode = dto.centreCode ?? dto.code ?? "";
    return {
        id: dto.id,
        code: dto.id ?? "",
        name: dto.name,
        countryId: dto.countryId ?? null,
        countryCode: dto.countryCode ?? "",
        currencyCode: dto.currencyCode ?? "",
        centreCode,
        telephoneNo,
        emergencyNo,
        centreAddress: dto.centreAddress ?? null,
        entityUpdate: !!dto.entityUpdate,
        active: isActive === undefined ? true : !!isActive,
        countryName: (dto as any).countryName
    };
}
